// Placeholder contact details and sample content — replace with real details before launch.
export const SITE = {
  name: 'Origo Private Limited',
  email: 'hello@origo.com',
  phone: '+1 (555) 019-2847',
  address: 'Remote-first · Serving clients worldwide',
  hours: 'Mon – Fri, 9:00 – 18:00',
};

export const NAV = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Services', to: '/services', mega: true },
  { label: 'Portfolio', to: '/portfolio' },
  { label: 'Process', to: '/process' },
  { label: 'Blog', to: '/blog' },
  { label: 'Life at Origo', to: '/life' },
  { label: 'Contact', to: '/contact' },
];

/* ------------------------------------------------------------------ services */

export const SERVICES = [
  {
    slug: 'web-development',
    icon: 'web',
    title: 'Web Development',
    desc: 'Fast, secure, SEO-ready websites and web apps built on modern frameworks.',
    tags: ['React / Next.js', 'WordPress', 'Laravel / Node'],
    tagline: 'Websites and web apps that load fast, rank well and convert.',
    overview: [
      'Your website is your hardest-working salesperson. We design and build responsive, accessible and lightning-fast websites — from marketing sites and company portals to complex web applications with user accounts, dashboards and integrations.',
      'Every build follows performance and SEO best practice from day one: clean semantic markup, optimised images, Core Web Vitals targets and structured data, so your site is easy to find as well as pleasant to use.',
    ],
    features: [
      { title: 'Corporate & marketing sites', desc: 'Brand-led websites with CMS editing so your team can update content without a developer.' },
      { title: 'Web applications & portals', desc: 'Customer portals, booking systems, dashboards and SaaS platforms with secure authentication.' },
      { title: 'CMS & headless builds', desc: 'WordPress, Strapi, Sanity or custom CMS — whichever fits your editorial workflow.' },
      { title: 'API & third-party integration', desc: 'Payment gateways, CRMs, ERPs, email tools and any REST or GraphQL service.' },
      { title: 'Performance & SEO', desc: 'Sub-2-second loads, schema markup, sitemaps and technical SEO baked in.' },
      { title: 'Maintenance & hosting', desc: 'Managed hosting, backups, security patches and uptime monitoring.' },
    ],
    deliverables: ['Responsive website or web app', 'Admin / CMS dashboard', 'Technical SEO setup', 'Analytics & tracking', 'Documentation and training'],
    tech: ['React', 'Next.js', 'Node.js', 'Laravel', 'WordPress', 'PostgreSQL', 'Tailwind CSS'],
    faqs: [
      { q: 'How long does a website take to build?', a: 'A typical marketing website takes 3–6 weeks. Larger web applications are scoped in phases so you see working software every couple of weeks.' },
      { q: 'Will I be able to edit the content myself?', a: 'Yes. We set up an easy content management system and train your team to use it.' },
    ],
  },
  {
    slug: 'mobile-app-development',
    icon: 'mobile',
    title: 'Mobile App Development',
    desc: 'Native and cross-platform apps for iOS and Android that people love to use.',
    tags: ['Flutter', 'React Native', 'iOS / Android'],
    tagline: 'Polished iOS and Android apps, from MVP to millions of users.',
    overview: [
      'We build mobile apps that feel native, run smoothly and are designed around how people actually use their phones. Whether you need a quick MVP to validate an idea or a full-scale platform, we choose the right technology for your goals and budget.',
      'Our cross-platform approach with Flutter and React Native lets you launch on iOS and Android together, while our native expertise covers hardware-heavy or performance-critical features.',
    ],
    features: [
      { title: 'iOS & Android apps', desc: 'Single-codebase or fully native builds, published to the App Store and Google Play.' },
      { title: 'MVP development', desc: 'Launch a focused first version in weeks, then iterate using real user feedback.' },
      { title: 'Backend & API', desc: 'Secure backends, real-time sync, push notifications and offline support.' },
      { title: 'Payments & subscriptions', desc: 'In-app purchases, Stripe and wallet integrations with secure checkout.' },
      { title: 'Device features', desc: 'Camera, GPS, Bluetooth, biometrics and wearable connections.' },
      { title: 'Store launch & ASO', desc: 'Store listings, screenshots, review handling and app store optimisation.' },
    ],
    deliverables: ['Production-ready app', 'Source code & repository', 'Backend / admin panel', 'Store submission', '30 days post-launch support'],
    tech: ['Flutter', 'React Native', 'Swift', 'Kotlin', 'Firebase', 'Node.js'],
    faqs: [
      { q: 'Native or cross-platform — which should I choose?', a: 'For most products cross-platform is faster and more cost-effective. We recommend native only when you need deep hardware access or maximum performance.' },
      { q: 'Do you publish the app for us?', a: 'Yes. We prepare the store listings and handle submission and review feedback on both stores.' },
    ],
  },
  {
    slug: 'ui-ux-design',
    icon: 'design',
    title: 'UI/UX Design',
    desc: 'Research-led interfaces, wireframes and prototypes that convert visitors.',
    tags: ['Figma', 'Prototyping', 'Design Systems'],
    tagline: 'Interfaces people understand instantly and enjoy using.',
    overview: [
      'Great products start with understanding people. Our designers combine user research, information architecture and visual design to create interfaces that are clear, accessible and on-brand.',
      'We work in Figma with interactive prototypes you can click through before a single line of code is written, which saves time, reduces rework and gets stakeholders aligned early.',
    ],
    features: [
      { title: 'User research & strategy', desc: 'Interviews, competitor analysis, personas and journey maps.' },
      { title: 'Wireframes & prototypes', desc: 'Low- and high-fidelity flows you can test with real users.' },
      { title: 'Visual & interface design', desc: 'Beautiful, consistent screens for web, mobile and dashboards.' },
      { title: 'Design systems', desc: 'Reusable components and guidelines that keep your product consistent as it grows.' },
      { title: 'Usability testing', desc: 'Moderated and remote testing to find friction before launch.' },
      { title: 'Accessibility audits', desc: 'WCAG-aligned reviews so your product works for everyone.' },
    ],
    deliverables: ['Research summary', 'User flows & wireframes', 'Clickable Figma prototype', 'UI kit & design system', 'Developer hand-off specs'],
    tech: ['Figma', 'FigJam', 'Maze', 'Lottie', 'Storybook'],
    faqs: [
      { q: 'Can you redesign an existing product?', a: 'Absolutely. We start with an audit of your current experience and prioritise the changes that will move your metrics most.' },
      { q: 'Do you hand over the design files?', a: 'Yes — you receive the complete Figma files and design system documentation.' },
    ],
  },
  {
    slug: 'graphic-design-branding',
    icon: 'brand',
    title: 'Graphic Design & Branding',
    desc: 'Logos, brand identities, social creatives and print collateral that stand out.',
    tags: ['Logo', 'Brand Kit', 'Packaging'],
    tagline: 'A brand identity that makes you instantly recognisable.',
    overview: [
      'Your brand is more than a logo — it is the feeling people get every time they meet your business. We craft complete identities, from strategy and naming to logo, colour, typography and tone of voice.',
      'We then apply that identity consistently across every touchpoint: website, social media, stationery, packaging, presentations and advertising.',
    ],
    features: [
      { title: 'Logo & identity design', desc: 'Distinctive logos with full variations for every use.' },
      { title: 'Brand guidelines', desc: 'Clear rules for colour, type, imagery and voice so your team stays on-brand.' },
      { title: 'Social media creatives', desc: 'Templates, post designs, covers and ad creatives.' },
      { title: 'Print & packaging', desc: 'Brochures, business cards, labels, packaging and signage.' },
      { title: 'Pitch & presentation design', desc: 'Investor decks and sales presentations that persuade.' },
      { title: 'Rebranding', desc: 'Evolve an existing brand without losing the equity you have built.' },
    ],
    deliverables: ['Logo suite (all formats)', 'Brand guideline PDF', 'Colour & typography system', 'Stationery & social templates', 'Source files'],
    tech: ['Illustrator', 'Photoshop', 'InDesign', 'Figma', 'Canva'],
    faqs: [
      { q: 'How many logo concepts do I get?', a: 'We present three distinct concepts, then refine your chosen direction with unlimited feedback rounds within the project scope.' },
      { q: 'Do I own the logo?', a: 'Yes. Full ownership and all source files transfer to you on final payment.' },
    ],
  },
  {
    slug: 'animation-motion-graphics',
    icon: 'animation',
    title: '2D / 3D Animation',
    desc: 'Explainer videos, motion graphics and 3D visuals that tell your story.',
    tags: ['Explainers', 'Motion Graphics', '3D Models'],
    tagline: 'Complex ideas, told in motion — memorable, shareable, effective.',
    overview: [
      'Animation turns abstract ideas into stories people remember. Our motion team produces 2D explainers, product animations, 3D renders and logo reveals that explain what you do in under two minutes.',
      'From script and storyboard to voice-over, illustration, animation and sound design, we handle the entire production so you get a finished, ready-to-publish video.',
    ],
    features: [
      { title: '2D explainer videos', desc: 'Character and infographic animation that simplifies your product or service.' },
      { title: '3D product visuals', desc: 'Photoreal renders and turntables for products, interiors and architecture.' },
      { title: 'Motion graphics', desc: 'Kinetic typography, logo stings, social videos and ad spots.' },
      { title: 'UI & app animation', desc: 'Lottie and micro-interactions that bring interfaces to life.' },
      { title: 'Script & storyboard', desc: 'Professional copywriting and visual planning before production starts.' },
      { title: 'Voice-over & sound', desc: 'Studio-quality voice talent, music and sound effects.' },
    ],
    deliverables: ['Script & storyboard', 'Final video (4K / HD)', 'Social media cut-downs', 'Project source files', 'Subtitles on request'],
    tech: ['After Effects', 'Blender', 'Cinema 4D', 'Premiere Pro', 'Lottie'],
    faqs: [
      { q: 'How long does an explainer video take?', a: 'A 60–90 second explainer typically takes 3–5 weeks from script approval to final delivery.' },
      { q: 'Can you animate our existing brand characters?', a: 'Yes. We can work from your existing assets or create new characters aligned with your brand.' },
    ],
  },
  {
    slug: 'ecommerce-solutions',
    icon: 'cart',
    title: 'E-Commerce Solutions',
    desc: 'Online stores with smooth checkout, inventory sync and payment integrations.',
    tags: ['Shopify', 'WooCommerce', 'Custom Carts'],
    tagline: 'Online stores built to turn browsers into repeat customers.',
    overview: [
      'We build e-commerce stores that are fast, easy to manage and optimised for conversion — from a single-product launch to multi-vendor marketplaces with thousands of SKUs.',
      'Every store includes frictionless checkout, secure payments, inventory and shipping integration, and the analytics you need to understand what sells and why.',
    ],
    features: [
      { title: 'Shopify & WooCommerce', desc: 'Custom themes and apps on the world’s most popular platforms.' },
      { title: 'Custom & headless stores', desc: 'Bespoke storefronts for unique catalogue or pricing needs.' },
      { title: 'Payment gateways', desc: 'Stripe, PayPal, local wallets, buy-now-pay-later and COD.' },
      { title: 'Inventory & ERP sync', desc: 'Automatic stock, order and accounting synchronisation.' },
      { title: 'Conversion optimisation', desc: 'Faster checkout, abandoned-cart recovery and A/B testing.' },
      { title: 'Marketplace builds', desc: 'Multi-vendor platforms with commissions and vendor dashboards.' },
    ],
    deliverables: ['Fully working store', 'Product import & setup', 'Payment & shipping config', 'Admin training', 'Launch checklist'],
    tech: ['Shopify', 'WooCommerce', 'Stripe', 'Next.js', 'Node.js'],
    faqs: [
      { q: 'Which platform should I choose?', a: 'Shopify suits most brands for speed to market; WooCommerce or custom builds suit complex requirements. We recommend based on your catalogue and growth plans.' },
      { q: 'Can you migrate my existing store?', a: 'Yes — products, customers, orders and SEO redirects are migrated with zero data loss.' },
    ],
  },
  {
    slug: 'custom-software-development',
    icon: 'code',
    title: 'Custom Software',
    desc: 'CRMs, ERPs, portals and internal tools tailored to how your business works.',
    tags: ['SaaS', 'Dashboards', 'APIs'],
    tagline: 'Software shaped around your workflow, not the other way round.',
    overview: [
      'Off-the-shelf tools rarely fit perfectly. We design and build custom software — CRMs, ERPs, booking engines, logistics and inventory systems, and full SaaS products — that automates your processes and scales with you.',
      'We start by mapping your workflows, then deliver in short, demonstrable iterations so you can steer the product as it takes shape.',
    ],
    features: [
      { title: 'SaaS product development', desc: 'Multi-tenant platforms with subscriptions, roles and analytics.' },
      { title: 'CRM & ERP systems', desc: 'Sales, HR, finance and operations tools tailored to your teams.' },
      { title: 'Workflow automation', desc: 'Replace spreadsheets and manual hand-offs with automated flows.' },
      { title: 'Dashboards & reporting', desc: 'Live KPIs and exportable reports for decision makers.' },
      { title: 'Legacy modernisation', desc: 'Move ageing systems to modern, maintainable architecture.' },
      { title: 'API development', desc: 'Secure, documented APIs for partners, apps and integrations.' },
    ],
    deliverables: ['Technical specification', 'Working software in sprints', 'Role-based access control', 'API documentation', 'Deployment & handover'],
    tech: ['Node.js', 'Python', 'Laravel', 'PostgreSQL', 'React', 'Docker'],
    faqs: [
      { q: 'How do you price custom software?', a: 'After a discovery phase we provide a fixed price per milestone, or a monthly team retainer for ongoing product work.' },
      { q: 'Do we own the source code?', a: 'Yes, completely, from day one.' },
    ],
  },
  {
    slug: 'seo-digital-marketing',
    icon: 'seo',
    title: 'SEO & Digital Marketing',
    desc: 'Data-driven search, paid and content campaigns that grow qualified traffic.',
    tags: ['SEO', 'Google Ads', 'Analytics'],
    tagline: 'Be found by the right people, at the moment they are searching.',
    overview: [
      'Traffic is only valuable when it turns into customers. Our digital marketing team builds integrated campaigns across search, paid media and content, all measured against revenue — not vanity metrics.',
      'We begin with a full audit of your site, competitors and keywords, then deliver a clear roadmap with monthly reporting so you always know what your investment is achieving.',
    ],
    features: [
      { title: 'Technical & on-page SEO', desc: 'Site audits, speed, structure, schema and content optimisation.' },
      { title: 'Content marketing', desc: 'Keyword-led blog posts, landing pages and guides that rank and convert.' },
      { title: 'Google & Meta Ads', desc: 'Search, display, shopping and social ads with continuous optimisation.' },
      { title: 'Link building', desc: 'Ethical outreach and digital PR that builds authority.' },
      { title: 'Email & automation', desc: 'Lifecycle flows, newsletters and CRM-driven campaigns.' },
      { title: 'Analytics & reporting', desc: 'GA4, tag management and dashboards tied to leads and sales.' },
    ],
    deliverables: ['SEO & competitor audit', 'Keyword & content roadmap', 'Campaign setup', 'Monthly performance report', 'Quarterly strategy review'],
    tech: ['Google Analytics 4', 'Search Console', 'Ahrefs', 'Google Ads', 'Meta Ads', 'Mailchimp'],
    faqs: [
      { q: 'How long until SEO shows results?', a: 'Most sites see meaningful movement within 3–6 months; paid campaigns can drive traffic within days.' },
      { q: 'Do you guarantee rankings?', a: 'No reputable agency can. We commit to transparent work, clear KPIs and monthly reporting.' },
    ],
  },
  {
    slug: 'social-media-marketing',
    icon: 'social',
    title: 'Social Media Marketing',
    desc: 'Content calendars, community management and campaigns across every platform.',
    tags: ['Content', 'Ads', 'Reels'],
    tagline: 'Build an audience that listens, engages and buys.',
    overview: [
      'Social media works when it is consistent, creative and connected to your business goals. We plan, produce and publish content that fits each platform, and manage conversations with your community.',
      'From organic growth to paid campaigns and influencer partnerships, we build a presence that supports your brand and drives measurable enquiries.',
    ],
    features: [
      { title: 'Strategy & content calendar', desc: 'Monthly themes and posting schedules aligned to your goals.' },
      { title: 'Content creation', desc: 'Graphics, short-form video, reels and carousels.' },
      { title: 'Community management', desc: 'Replies, moderation and reputation care.' },
      { title: 'Paid social campaigns', desc: 'Targeted ads on Facebook, Instagram, LinkedIn, TikTok and YouTube.' },
      { title: 'Influencer partnerships', desc: 'Sourcing, briefing and measuring creator collaborations.' },
      { title: 'Social analytics', desc: 'Growth, engagement and conversion reporting.' },
    ],
    deliverables: ['Platform strategy', 'Monthly content calendar', 'Designed posts & videos', 'Ad campaigns', 'Monthly insights report'],
    tech: ['Meta Business Suite', 'LinkedIn Ads', 'TikTok Ads', 'Buffer', 'Canva', 'CapCut'],
    faqs: [
      { q: 'Which platforms should we be on?', a: 'We recommend platforms based on where your customers spend time — not every network is right for every brand.' },
      { q: 'Do you need access to our accounts?', a: 'We work through secure partner access so you always keep full ownership.' },
    ],
  },
  {
    slug: 'cloud-devops',
    icon: 'cloud',
    title: 'Cloud & DevOps',
    desc: 'Scalable cloud infrastructure, CI/CD pipelines and 24/7 monitoring.',
    tags: ['AWS / Azure', 'Docker', 'CI/CD'],
    tagline: 'Infrastructure that scales quietly and ships safely.',
    overview: [
      'Reliable software needs reliable infrastructure. Our DevOps engineers design cloud architectures that handle growth, automate deployments and keep your costs predictable.',
      'We set up CI/CD pipelines, infrastructure-as-code, monitoring and alerting so releases become routine and incidents are caught before customers notice.',
    ],
    features: [
      { title: 'Cloud architecture', desc: 'AWS, Azure and Google Cloud designs for scale and resilience.' },
      { title: 'CI/CD pipelines', desc: 'Automated build, test and deployment workflows.' },
      { title: 'Containers & Kubernetes', desc: 'Docker-based deployments and orchestration.' },
      { title: 'Monitoring & alerting', desc: 'Dashboards, logs and on-call alerts.' },
      { title: 'Security & compliance', desc: 'Hardening, backups, secrets management and audits.' },
      { title: 'Cost optimisation', desc: 'Right-sizing and reserved capacity to cut cloud bills.' },
    ],
    deliverables: ['Architecture diagram', 'Infrastructure-as-code', 'CI/CD pipeline', 'Monitoring dashboards', 'Runbooks'],
    tech: ['AWS', 'Azure', 'Docker', 'Kubernetes', 'Terraform', 'GitHub Actions'],
    faqs: [
      { q: 'Can you migrate us to the cloud?', a: 'Yes. We plan and execute migrations with minimal downtime and a rollback strategy.' },
      { q: 'Do you offer ongoing support?', a: 'We provide managed DevOps retainers with monitoring and incident response.' },
    ],
  },
  {
    slug: 'ai-automation',
    icon: 'ai',
    title: 'AI & Automation',
    desc: 'Chatbots, workflow automation and AI features that save hours every week.',
    tags: ['Chatbots', 'Automation', 'Integrations'],
    tagline: 'Practical AI that removes repetitive work and improves decisions.',
    overview: [
      'AI is most valuable when it solves a specific business problem. We help you identify the processes where automation and machine learning will save the most time, then build and integrate the solution into your existing tools.',
      'From customer-support chatbots and document processing to recommendation engines and data pipelines, we focus on reliable, secure and measurable results.',
    ],
    features: [
      { title: 'AI chatbots & assistants', desc: 'Support and sales assistants trained on your own knowledge.' },
      { title: 'Workflow automation', desc: 'Connect apps and automate approvals, reports and data entry.' },
      { title: 'Document processing', desc: 'Extract and classify data from invoices, forms and contracts.' },
      { title: 'Predictive analytics', desc: 'Forecasting, scoring and recommendation models.' },
      { title: 'LLM integration', desc: 'Add generative AI features safely into your product.' },
      { title: 'Data engineering', desc: 'Clean, reliable pipelines that power analytics and AI.' },
    ],
    deliverables: ['Opportunity assessment', 'Working AI solution', 'System integration', 'Usage analytics', 'Team training'],
    tech: ['Python', 'OpenAI / Claude APIs', 'LangChain', 'n8n / Zapier', 'TensorFlow', 'PostgreSQL'],
    faqs: [
      { q: 'Is my data safe with AI tools?', a: 'We design for privacy first — private deployments, data minimisation and clear access controls.' },
      { q: 'What will AI automation save us?', a: 'We quantify expected hours saved during the assessment so you can see the return before you commit.' },
    ],
  },
  {
    slug: 'qa-software-testing',
    icon: 'qa',
    title: 'QA & Software Testing',
    desc: 'Manual and automated testing so every release ships bug-free and fast.',
    tags: ['Automation', 'Performance', 'Security'],
    tagline: 'Find the bugs before your customers do.',
    overview: [
      'Quality is not a final step — it is built into every stage. Our QA engineers design test strategies, run thorough manual testing and build automation suites that protect every release.',
      'We cover functional, regression, cross-browser, mobile, performance and security testing, giving you the confidence to ship faster.',
    ],
    features: [
      { title: 'Manual & exploratory testing', desc: 'Human-led testing of real user journeys and edge cases.' },
      { title: 'Test automation', desc: 'Cypress, Playwright and Selenium suites integrated into CI.' },
      { title: 'Mobile & cross-browser', desc: 'Coverage across devices, OS versions and browsers.' },
      { title: 'Performance testing', desc: 'Load and stress tests to prove you can handle peak traffic.' },
      { title: 'Security testing', desc: 'Vulnerability scans and OWASP-based penetration checks.' },
      { title: 'API testing', desc: 'Contract and integration tests for every endpoint.' },
    ],
    deliverables: ['Test plan & cases', 'Bug reports with evidence', 'Automation suite', 'Performance report', 'Release sign-off'],
    tech: ['Cypress', 'Playwright', 'Selenium', 'Postman', 'JMeter', 'BrowserStack'],
    faqs: [
      { q: 'Can you test software someone else built?', a: 'Yes. We regularly audit and test third-party and in-house applications.' },
      { q: 'Do you integrate with our sprint process?', a: 'Our testers join your sprints and report in Jira, Linear or any tracker you use.' },
    ],
  },
];

export const EXTRA_SERVICES = ['IT Consulting', 'Technical Support', 'Shopify Stores', 'WordPress Themes', 'Video Production', 'Content Writing', 'App Store Optimisation', 'Maintenance Plans'];

export const SERVICE_BY_SLUG = Object.fromEntries(SERVICES.map((s) => [s.slug, s]));

/* ------------------------------------------------------------- company info */

export const STATS = [
  { value: 120, suffix: '+', label: 'Projects delivered' },
  { value: 60, suffix: '+', label: 'Happy clients' },
  { value: 9, suffix: '', label: 'Years of experience' },
  { value: 40, suffix: '+', label: 'Specialists on the team' },
];

export const WHY = [
  { icon: 'team', title: 'Senior, dedicated team', desc: 'Experienced engineers and designers who own your project from kickoff to launch.' },
  { icon: 'clock', title: 'On-time delivery', desc: 'Clear milestones, weekly demos and deadlines we actually keep.' },
  { icon: 'shield', title: 'Secure & reliable', desc: 'Security reviews, backups and best practices built into every build.' },
  { icon: 'chart', title: 'Built to grow', desc: 'Architecture and design chosen for where your business is going, not just today.' },
  { icon: 'headset', title: 'Ongoing support', desc: 'Maintenance and priority support plans long after launch day.' },
  { icon: 'rocket', title: 'Transparent pricing', desc: 'Fixed quotes or monthly retainers. No hidden costs, no surprises.' },
];

export const VALUES = [
  { icon: 'shield', title: 'Integrity', desc: 'We say what we will do and do what we say — including telling you when something is not the right call.' },
  { icon: 'rocket', title: 'Craft', desc: 'Details matter. We sweat the code, the pixels and the copy until it is something we are proud to put our name on.' },
  { icon: 'team', title: 'Partnership', desc: 'We work as an extension of your team, sharing context, credit and responsibility for outcomes.' },
  { icon: 'chart', title: 'Results', desc: 'Every decision is tied to a business goal. We measure success by your growth, not our hours.' },
];

export const TIMELINE = [
  { year: '2017', title: 'Founded', desc: 'Started with two engineers and a belief that agency work could be more transparent and more senior.' },
  { year: '2019', title: 'First enterprise client', desc: 'Rebuilt a logistics platform serving 40,000 daily users — still running today.' },
  { year: '2021', title: 'Design & animation studio', desc: 'Added dedicated UI/UX, branding and motion teams to offer one-stop digital delivery.' },
  { year: '2023', title: 'Distributed team of 40', desc: 'Grew into a remote-first team of specialists across engineering, design and marketing.' },
  { year: '2025', title: '120th project shipped', desc: 'Launched our AI & automation practice and a managed DevOps service.' },
];

export const TEAM = [
  { name: 'Sam Whitfield', role: 'Founder & CEO', bio: 'Engineer-turned-founder with 15 years building products for startups and enterprises.' },
  { name: 'Yuki Tanaka', role: 'Head of Design', bio: 'Leads UI/UX and brand. Former type designer, obsessed with detail.' },
  { name: 'Diego Alvarez', role: 'Lead Engineer', bio: 'Full-stack architect and open-source contributor focused on scalable systems.' },
  { name: 'Aisha Rahman', role: 'Product Strategist', bio: 'Turns business goals into clear roadmaps and measurable outcomes.' },
  { name: 'Omar Haddad', role: 'Head of Animation', bio: 'Directs 2D and 3D productions from storyboard to final render.' },
  { name: 'Priya Menon', role: 'Marketing Lead', bio: 'Builds SEO and paid-media programmes that compound over time.' },
];

export const INDUSTRIES = ['Healthcare', 'Fintech', 'E-Commerce & Retail', 'Education', 'Logistics', 'Real Estate', 'SaaS & Startups', 'Hospitality'];

export const PROCESS = [
  { n: '01', title: 'Discover', desc: 'We learn your goals, audience and constraints through a focused discovery workshop.', points: ['Stakeholder interviews', 'Competitor & market review', 'Goals, KPIs and scope', 'Clear proposal and timeline'] },
  { n: '02', title: 'Plan & Design', desc: 'Roadmap, wireframes and interactive prototypes you can click through and approve.', points: ['Information architecture', 'Wireframes & UI design', 'Clickable prototype', 'Technical architecture'] },
  { n: '03', title: 'Build', desc: 'Agile sprints with weekly demos, clean code and continuous testing.', points: ['Two-week sprints', 'Weekly demos & feedback', 'Code review & automated tests', 'Shared staging environment'] },
  { n: '04', title: 'Launch', desc: 'Deployment, performance tuning and a smooth, monitored go-live.', points: ['Final QA & security checks', 'Performance tuning', 'Deployment & DNS cut-over', 'Team training'] },
  { n: '05', title: 'Support & Grow', desc: 'Maintenance, analytics and iteration to keep compounding results.', points: ['Monitoring & maintenance', 'Analytics reviews', 'Feature roadmap', 'Priority support'] },
];

export const ENGAGEMENT = [
  { title: 'Fixed price', desc: 'Best for well-defined projects. One quote, clear milestones, no surprises.', points: ['Defined scope & timeline', 'Milestone-based payments', 'Ideal for websites & MVPs'] },
  { title: 'Dedicated team', desc: 'A cross-functional pod working only on your product, month to month.', points: ['Engineers, designers & PM', 'Flexible scale up / down', 'Ideal for evolving products'], highlight: true },
  { title: 'Time & materials', desc: 'Pay for the hours you use. Maximum flexibility for changing requirements.', points: ['Transparent weekly reports', 'Change scope any time', 'Ideal for support & R&D'] },
];

/* ---------------------------------------------------------------- portfolio */

export const PROJECTS = [
  {
    slug: 'northwind-analytics', name: 'Northwind Analytics', cat: 'Web', tag: 'SaaS Dashboard', kind: 'dashboard', year: '2025', duration: '10 weeks', client: 'Northwind Analytics',
    desc: 'Real-time analytics platform replacing a legacy reporting tool.',
    challenge: 'Finance teams were waiting hours for reports from a slow legacy tool and exporting data into spreadsheets to make decisions.',
    solution: 'We designed and built a real-time analytics dashboard with customisable widgets, role-based access and scheduled exports, backed by a scalable data pipeline.',
    results: [{ value: '+180%', label: 'demo conversion' }, { value: '400ms', label: 'p95 load time' }, { value: '12k', label: 'daily active users' }],
    services: ['web-development', 'ui-ux-design', 'cloud-devops'], tech: ['React', 'Node.js', 'PostgreSQL', 'AWS'],
  },
  {
    slug: 'orbit-health', name: 'Orbit Health', cat: 'Mobile', tag: 'Patient App', kind: 'phone', year: '2025', duration: '12 weeks', client: 'Orbit Health',
    desc: 'Appointment and care-plan app for a multi-clinic network.',
    challenge: 'A network of 40 clinics suffered high no-show rates and relied on phone calls for scheduling and reminders.',
    solution: 'A cross-platform patient app with online booking, smart reminders, secure messaging and care-plan tracking, integrated with the clinics’ existing records system.',
    results: [{ value: '-32%', label: 'no-shows' }, { value: '4.8★', label: 'app store rating' }, { value: '60k', label: 'installs in 3 months' }],
    services: ['mobile-app-development', 'ui-ux-design', 'qa-software-testing'], tech: ['Flutter', 'Firebase', 'Node.js'],
  },
  {
    slug: 'lumen-commerce', name: 'Lumen Commerce', cat: 'Web', tag: 'E-Commerce', kind: 'store', year: '2024', duration: '8 weeks', client: 'Lumen Commerce',
    desc: 'Headless storefront built to handle peak-season traffic.',
    challenge: 'The existing store slowed to a crawl during sales and checkout drop-off was costing revenue.',
    solution: 'A headless storefront with edge caching, a streamlined one-page checkout and inventory sync, load-tested for 12× normal traffic.',
    results: [{ value: '12×', label: 'traffic handled' }, { value: '99.99%', label: 'uptime' }, { value: '+41%', label: 'checkout completion' }],
    services: ['ecommerce-solutions', 'web-development', 'seo-digital-marketing'], tech: ['Next.js', 'Shopify', 'Stripe', 'Vercel'],
  },
  {
    slug: 'atlas-rebrand', name: 'Atlas Rebrand', cat: 'Design', tag: 'Brand Identity', kind: 'brand', year: '2024', duration: '6 weeks', client: 'Atlas Co.',
    desc: 'Complete identity system, guidelines and launch collateral.',
    challenge: 'A fast-growing company outgrew its early logo and had inconsistent branding across product, sales and social.',
    solution: 'A fresh visual identity — logo, colour, typography, illustration style and tone of voice — delivered with comprehensive guidelines and ready-to-use templates.',
    results: [{ value: '3 wks', label: 'to full system' }, { value: '40+', label: 'templates delivered' }, { value: '$18M', label: 'round closed after launch' }],
    services: ['graphic-design-branding', 'ui-ux-design'], tech: ['Illustrator', 'Figma', 'After Effects'],
  },
  {
    slug: 'halo-explainer', name: 'Halo Explainer', cat: 'Animation', tag: '2D Motion', kind: 'motion', year: '2025', duration: '5 weeks', client: 'Halo Labs',
    desc: 'Animated product explainer that tripled demo sign-ups.',
    challenge: 'Halo’s technology was hard to explain in text, and prospects were leaving the homepage before understanding the value.',
    solution: 'A 75-second 2D explainer with custom illustration, voice-over and sound design, plus social cut-downs for paid campaigns.',
    results: [{ value: '3×', label: 'demo sign-ups' }, { value: '+64%', label: 'time on page' }, { value: '1.2M', label: 'views on social' }],
    services: ['animation-motion-graphics', 'graphic-design-branding', 'social-media-marketing'], tech: ['After Effects', 'Illustrator', 'Premiere Pro'],
  },
  {
    slug: 'ferro-logistics', name: 'Ferro Logistics', cat: 'Mobile', tag: 'Fleet App', kind: 'phone', year: '2023', duration: '14 weeks', client: 'Ferro Logistics',
    desc: 'Driver and dispatch app with live route optimisation.',
    challenge: 'Dispatchers juggled three disconnected spreadsheets and drivers lacked live route information.',
    solution: 'A driver app and web dispatch console with live GPS tracking, automated route optimisation and proof-of-delivery capture.',
    results: [{ value: '9 hrs', label: 'saved per week' }, { value: '40k', label: 'daily deliveries' }, { value: '-18%', label: 'fuel costs' }],
    services: ['mobile-app-development', 'custom-software-development', 'cloud-devops'], tech: ['React Native', 'Node.js', 'PostgreSQL', 'AWS'],
  },
];
export const PROJECT_BY_SLUG = Object.fromEntries(PROJECTS.map((p) => [p.slug, p]));
export const PROJECT_FILTERS = ['All', 'Web', 'Mobile', 'Design', 'Animation'];

export const TECH = ['React', 'Next.js', 'Node.js', 'TypeScript', 'Flutter', 'React Native', 'Laravel', 'Python', 'Figma', 'Blender', 'After Effects', 'AWS', 'Docker', 'PostgreSQL', 'Shopify', 'WordPress'];

export const QUOTES = [
  { quote: 'Origo rebuilt our platform in eight weeks and it has not gone down since. Communication was excellent throughout.', name: 'Elena Cross', role: 'CEO, Northwind Analytics' },
  { quote: 'They think like partners, not vendors. Every recommendation was tied to a metric we actually cared about.', name: 'Marcus Diallo', role: 'Head of Product, Orbit Health' },
  { quote: 'The most professional team we have worked with. Design, development and animation all under one roof.', name: 'Priya Natarajan', role: 'Founder, Lumen Commerce' },
];

export const FAQS = [
  { q: 'How quickly can you start a project?', a: 'Most engagements kick off within one to two weeks of signing. For urgent work we can often start within 48 hours once the scope is clear.' },
  { q: 'Do you work with startups and small businesses?', a: 'Yes. We work with everyone from first-time founders to established enterprises, and scope each project to fit your budget and stage.' },
  { q: 'Who owns the source code and designs?', a: 'You do — 100% from day one. All code, design files and assets are handed over at the end of the project.' },
  { q: 'Do you offer fixed-price quotes?', a: 'Yes. For well-defined projects we provide a fixed quote; for ongoing work we offer flexible monthly retainers.' },
  { q: 'Do you provide support after launch?', a: 'Absolutely. We offer maintenance and support plans covering updates, monitoring, bug fixes and new features.' },
  { q: 'Which time zones do you work in?', a: 'We are remote-first with overlap across Europe, the Middle East, Asia and the Americas, and schedule meetings around your working hours.' },
];

/* --------------------------------------------------------------------- blog */

export const POSTS = [
  {
    slug: 'how-much-does-a-website-cost', title: 'How much does a business website really cost?', cat: 'Web', date: 'Sep 12, 2026', read: '6 min read',
    excerpt: 'A practical breakdown of what drives website pricing, and how to budget without overpaying.',
    body: [
      'Website prices range from a few hundred dollars to six figures, and the gap usually comes down to scope, not quality alone. Understanding what you are paying for is the best way to budget well.',
      'The biggest cost drivers are the number of unique page templates, custom functionality such as booking or accounts, integrations with other systems, and the amount of bespoke design and content work involved.',
      'A simple marketing site with a CMS is the most affordable option. Adding e-commerce, multilingual content or a customer portal increases both design and engineering effort, but also the return the site can generate.',
      'Our advice: start with a clear goal, build the smallest version that achieves it, and plan phases for the rest. A fixed-price first phase keeps risk low while you validate what your customers actually use.',
    ],
  },
  {
    slug: 'native-vs-cross-platform-apps', title: 'Native vs cross-platform apps: how to choose', cat: 'Mobile', date: 'Aug 28, 2026', read: '5 min read',
    excerpt: 'Flutter, React Native or fully native? A simple framework for making the right call.',
    body: [
      'Choosing a mobile technology is one of the first big decisions in an app project, and it affects cost, speed and future flexibility.',
      'Cross-platform frameworks like Flutter and React Native let one team build for iOS and Android from a shared codebase. This typically reduces cost and time to market by 30–40% and keeps both apps in sync.',
      'Native development still wins when you need the very latest platform features, heavy 3D or AR, or tight hardware integration. In those cases the extra investment pays back in performance and polish.',
      'For most business apps — booking, commerce, on-demand services, internal tools — cross-platform is the pragmatic choice. We usually recommend it unless a specific requirement says otherwise.',
    ],
  },
  {
    slug: 'brand-identity-checklist', title: 'The brand identity checklist for growing companies', cat: 'Design', date: 'Aug 04, 2026', read: '4 min read',
    excerpt: 'Ten things every brand needs before spending a dollar on advertising.',
    body: [
      'A strong brand makes every marketing dollar work harder. Before you invest in ads, make sure the foundations are in place.',
      'Start with clarity: who you serve, what you promise and how you are different. Then translate that into a logo system, colour palette, typography and a consistent tone of voice.',
      'Document it. Brand guidelines prevent drift as your team grows, and templates for social, sales decks and documents save hours every week.',
      'Finally, test your identity in the real world — on your website, packaging and social profiles — before locking it in. Great brands are consistent, but they also stay flexible enough to grow.',
    ],
  },
  {
    slug: 'why-explainer-videos-work', title: 'Why explainer videos still convert so well', cat: 'Animation', date: 'Jul 22, 2026', read: '4 min read',
    excerpt: 'Short animated videos explain complex products faster than any page of text.',
    body: [
      'People process visuals dramatically faster than text, which is why a well-made explainer can turn a confusing product into an obvious solution in under two minutes.',
      'The best explainers follow a simple structure: a relatable problem, your solution, how it works in three steps and a clear call to action.',
      'Keep it short — 60 to 90 seconds — and design for sound-off viewing with captions, since much of your audience will watch on mobile in public.',
      'Repurpose the footage into short social clips, landing page headers and sales email thumbnails to get far more value from a single production.',
    ],
  },
  {
    slug: 'seo-basics-for-new-websites', title: 'SEO basics every new website should get right', cat: 'Marketing', date: 'Jul 03, 2026', read: '5 min read',
    excerpt: 'Technical foundations and content habits that help new sites get discovered.',
    body: [
      'Search engine optimisation is far easier to build in from the start than to bolt on later. A handful of fundamentals make the biggest difference for new sites.',
      'First, ensure the site is fast, mobile-friendly and has clean URLs, descriptive page titles and meta descriptions. Submit a sitemap and verify the site in Search Console.',
      'Second, publish content that answers real questions your customers ask. One genuinely helpful page often outperforms ten thin ones.',
      'Finally, earn trust: collect reviews, get listed in relevant directories and create content others want to link to. SEO rewards patience and consistency.',
    ],
  },
  {
    slug: 'when-to-invest-in-devops', title: 'When should a startup invest in DevOps?', cat: 'Engineering', date: 'Jun 18, 2026', read: '5 min read',
    excerpt: 'You do not need Kubernetes on day one, but you do need these basics.',
    body: [
      'Many early-stage teams delay DevOps until problems appear, and then lose weeks to firefighting. A little investment early prevents most of that pain.',
      'At a minimum, set up version control with protected branches, automated tests and a one-click deployment pipeline. These cost little and pay off immediately.',
      'As traffic grows, add monitoring, alerting, backups and infrastructure-as-code so your environment is reproducible and recoverable.',
      'Reserve heavier tools like Kubernetes for when you genuinely need them. Simplicity is a feature — choose the least complex setup that meets today’s needs and tomorrow’s growth.',
    ],
  },
];
export const POST_BY_SLUG = Object.fromEntries(POSTS.map((p) => [p.slug, p]));
